import {
  Area, AreaChart as RAreaChart, Bar, BarChart as RBarChart,
  CartesianGrid, Cell, Line, LineChart as RLineChart, Pie, PieChart as RPieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ChartFrame, VizTooltip, type Series } from "./ChartFrame";
import { useVizTokens } from "./useVizTokens";

type Row = Record<string, string | number>;

type Common = {
  data: Row[];
  /** The categorical field, e.g. "month". */
  xKey: string;
  /**
   * Series in fixed order. Slot colours are assigned by index and never cycled.
   * A ninth series is not supported on purpose: fold it into "Other" or facet.
   */
  series: Series[];
  title?: string;
  subtitle?: string;
  height?: number;
  format?: (v: number | string) => string;
  /** Renders the accessible table fallback under the chart. */
  showTable?: boolean;
};

const MAX_SLOTS = 8;

function useSlots(series: Series[]) {
  const t = useVizTokens();
  if (series.length > MAX_SLOTS) {
    // Loud rather than silently cycling back to slot 1, which would make two
    // different entities the same colour.
    console.warn(
      `[spec-design-system] ${series.length} series passed but only ${MAX_SLOTS} categorical slots exist. ` +
      `Fold the tail into "Other" or use small multiples. Colours are never cycled.`
    );
  }
  return { t, colors: series.map((_, i) => t.series[i % MAX_SLOTS]) };
}

const axisProps = (t: ReturnType<typeof useVizTokens>) => ({
  stroke: t.axis,
  tick: { fill: t.label, fontSize: 12, fontFamily: "var(--font-axis, var(--mono))" },
  tickLine: false,
  axisLine: { stroke: t.axis },
});

const tableFrom = (data: Row[], xKey: string, series: Series[], fmt?: (v: number | string) => string) => ({
  header: xKey,
  rows: data.map((d) => [String(d[xKey]), ...series.map((s) => (fmt ? fmt(d[s.key]) : d[s.key]))]),
});

/** Change over time. Crosshair tooltip, 2px strokes, markers only on hover. */
export function LineChart({ data, xKey, series, title, subtitle, height, format, showTable, dots }:
  Common & { /** Markers on every point: for short or gappy series, where a lone point would otherwise not draw. */ dots?: boolean }) {
  const { t, colors } = useSlots(series);
  return (
    <ChartFrame title={title} subtitle={subtitle} series={series} colors={colors} height={height}
                tableView={showTable ? tableFrom(data, xKey, series, format) : undefined}>
      <ResponsiveContainer width="100%" height="100%">
        <RLineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={t.grid} vertical={false} />
          <XAxis dataKey={xKey} {...axisProps(t)} interval="preserveStartEnd" minTickGap={18} />
          <YAxis {...axisProps(t)} tickFormatter={format ? (v) => format(v) : undefined} width={64} />
          <Tooltip content={<VizTooltip formatter={format} />} cursor={{ stroke: t.axis }} />
          {series.map((s, i) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={colors[i]} strokeWidth={t.lineWidth}
                  dot={dots ? { r: t.marker / 2, fill: colors[i], stroke: t.surface, strokeWidth: 2 } : false}
                  isAnimationActive={false}
                  activeDot={{ r: t.marker / 2, stroke: t.surface, strokeWidth: 2 }} />
          ))}
        </RLineChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/** Magnitude. Rounded data-end only, anchored to the baseline. */
export function BarChart({ data, xKey, series, title, subtitle, height, format, showTable, stacked, integer }:
  Common & { stacked?: boolean; /** Counts: whole-number ticks only. */ integer?: boolean }) {
  const { t, colors } = useSlots(series);
  return (
    <ChartFrame title={title} subtitle={subtitle} series={series} colors={colors} height={height}
                tableView={showTable ? tableFrom(data, xKey, series, format) : undefined}>
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} barGap={2} barCategoryGap="22%">
          <CartesianGrid stroke={t.grid} vertical={false} />
          <XAxis dataKey={xKey} {...axisProps(t)} interval="preserveStartEnd" minTickGap={18} />
          <YAxis {...axisProps(t)} tickFormatter={format ? (v) => format(v) : undefined} width={64} allowDecimals={!integer} />
          <Tooltip content={<VizTooltip formatter={format} />} cursor={{ fill: t.grid, fillOpacity: 0.5 }} />
          {series.map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={colors[i]}
                 stackId={stacked ? "a" : undefined}
                 // A 2px surface-coloured edge keeps stacked segments apart.
                 stroke={stacked ? t.surface : undefined} strokeWidth={stacked ? 2 : 0}
                 isAnimationActive={false}
                 // Round only the end of the bar. A pill floating off the axis is
                 // the classic tell of a chart nobody looked at.
                 radius={stacked && i < series.length - 1
                   ? 0
                   : [t.barRadius, t.barRadius, 0, 0]} />
          ))}
        </RBarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/** Cumulative magnitude over time. Single series reads best. */
export function AreaChart({ data, xKey, series, title, subtitle, height, format, showTable }: Common) {
  const { t, colors } = useSlots(series);
  return (
    <ChartFrame title={title} subtitle={subtitle} series={series} colors={colors} height={height}
                tableView={showTable ? tableFrom(data, xKey, series, format) : undefined}>
      <ResponsiveContainer width="100%" height="100%">
        <RAreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <defs>
            {series.map((s, i) => (
              <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors[i]} stopOpacity={0.22} />
                <stop offset="100%" stopColor={colors[i]} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke={t.grid} vertical={false} />
          <XAxis dataKey={xKey} {...axisProps(t)} interval="preserveStartEnd" minTickGap={18} />
          <YAxis {...axisProps(t)} tickFormatter={format ? (v) => format(v) : undefined} width={64} />
          <Tooltip content={<VizTooltip formatter={format} />} cursor={{ stroke: t.axis }} />
          {series.map((s, i) => (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={colors[i]} strokeWidth={t.lineWidth} fill={`url(#fill-${s.key})`}
                  isAnimationActive={false} />
          ))}
        </RAreaChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/**
 * Part-to-whole. Donut, not pie: the hole holds the total, which is the number
 * people actually want. Cap at five slices, everything else is "Other".
 */
export function DonutChart({ data, nameKey, valueKey, title, subtitle, height = 260, format, total }: {
  data: Row[]; nameKey: string; valueKey: string;
  title?: string; subtitle?: string; height?: number;
  format?: (v: number | string) => string;
  /** Rendered in the hole. Pass the pre-computed total, formatted. */
  total?: string;
}) {
  const series: Series[] = data.map((d) => ({ key: String(d[nameKey]), label: String(d[nameKey]) }));
  const { t, colors } = useSlots(series);
  return (
    <ChartFrame title={title} subtitle={subtitle} series={series} colors={colors} height={height}
                tableView={{ header: nameKey,
                  rows: data.map((d) => [String(d[nameKey]), format ? format(d[valueKey]) : d[valueKey]]) }}>
      <ResponsiveContainer width="100%" height="100%">
        <RPieChart>
          <Tooltip content={<VizTooltip formatter={format} />} />
          <Pie data={data} dataKey={valueKey} nameKey={nameKey}
               innerRadius="62%" outerRadius="86%" paddingAngle={2}
               stroke={t.surface} strokeWidth={2} isAnimationActive={false}>
            {data.map((_, i) => <Cell key={i} fill={colors[i]} />)}
          </Pie>
          {total && (
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
                  style={{ font: "600 22px var(--mono)", fill: t.text, fontVariantNumeric: "tabular-nums" }}>
              {total}
            </text>
          )}
        </RPieChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/** Inline trend for a stat tile. No axes, no legend, no tooltip. */
export function Sparkline({ data, valueKey, height = 34, slot = 0 }: {
  data: Row[]; valueKey: string; height?: number; slot?: number;
}) {
  const t = useVizTokens();
  return (
    <div style={{ width: "100%", height }} aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <RLineChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <Line type="monotone" dataKey={valueKey} stroke={t.series[slot]}
                strokeWidth={t.lineWidth} dot={false} isAnimationActive={false} />
        </RLineChart>
      </ResponsiveContainer>
    </div>
  );
}
